/**
 * @engine/vfx - VFX Compiler
 *
 * Main entry point for VFX graph compilation.
 * Orchestrates the compilation pipeline:
 *   VFXGraph → Extract Systems → Validate → Generate Slang → compile to WGSL → CompiledResult
 *
 * Compute stages are generated as Slang (function-per-node codegen, see
 * ./codegen + ./stages) and compiled through the injected Slang compile
 * service (SlangCompileService) — hence `compile()` is async.
 */

import type { VFXGraph } from '../VFXGraph';
import type {
  VFXCompiledSystem,
  VFXCompilationResult,
  VFXCompilationError,
  VFXCompilerOptions,
  VFXExtractedSystem,
} from './types';
import { DEFAULT_COMPILER_OPTIONS } from './types';
import { extractSystems } from './VFXSystemExtractor';
import { generateShaderBundle } from './ShaderGenerator';
import { generateParticleStruct } from './WGSLEmitter';

// ============================================================================
// COMPILER
// ============================================================================

/**
 * VFX Graph Compiler.
 * 
 * Compiles a VFXGraph into GPU-ready shader bundles.
 * 
 * @example
 * ```ts
 * const compiler = new VFXCompiler();
 * const result = compiler.compile(graph);
 * 
 * if (result.success) {
 *   for (const system of result.systems) {
 *     createRenderer(system);
 *   }
 * } else {
 *   console.error(result.errors);
 * }
 * ```
 */
export class VFXCompiler {
  private options: Required<VFXCompilerOptions>;
  
  constructor(options: VFXCompilerOptions = {}) {
    this.options = { ...DEFAULT_COMPILER_OPTIONS, ...options };
  }
  
  /**
   * Compile a VFX graph.
   * Async: generated compute stages are Slang, compiled to WGSL through the
   * injected compile service (see SlangCompileService).
   */
  async compile(graph: VFXGraph): Promise<VFXCompilationResult> {
    const errors: VFXCompilationError[] = [];
    const warnings: string[] = [];
    
    // Step 0: Validate context flow connections
    const flowErrors = this.validateContextFlow(graph);
    errors.push(...flowErrors);
    
    // If flow validation failed, return early with helpful message
    if (flowErrors.length > 0) {
      return {
        systems: [],
        particleStruct: generateParticleStruct(this.options.attributes),
        errors,
        warnings,
        success: false,
      };
    }
    
    // Step 1: Extract systems from graph
    const extraction = extractSystems(graph);
    errors.push(...extraction.errors);
    warnings.push(...extraction.warnings);
    
    // If extraction failed, return early
    if (extraction.systems.length === 0 || errors.length > 0) {
      return {
        systems: [],
        particleStruct: generateParticleStruct(this.options.attributes),
        errors,
        warnings,
        success: false,
      };
    }
    
    // Step 2: Validate each system
    for (const system of extraction.systems) {
      const validationErrors = this.validateSystem(system);
      errors.push(...validationErrors);
    }
    
    // If validation failed and strict mode, return early
    if (errors.length > 0) {
      return {
        systems: [],
        particleStruct: generateParticleStruct(this.options.attributes),
        errors,
        warnings,
        success: false,
      };
    }
    
    // Step 3: Generate shaders for each system
    const compiledSystems: VFXCompiledSystem[] = [];
    
    for (const system of extraction.systems) {
      try {
        const shaders = await generateShaderBundle(system, graph, this.options);
        warnings.push(...shaders.warnings);

        const compiled: VFXCompiledSystem = {
          id: system.id,
          name: system.name,
          maxParticles: system.maxParticles,
          spawn: system.spawn.config,
          initShader: shaders.initShader,
          updateShader: shaders.updateShader,
          vertexShader: shaders.vertexShader,
          fragmentShader: shaders.fragmentShader,
          inputEvent: system.inputEvent,
          outputEvent: system.outputEvent,
          usesTexture: shaders.usesTexture,
          textureSlot: shaders.textureSlot,
          tweaks: shaders.tweaks,
          emitsLight: shaders.emitsLight,
          lightRenderQuad: shaders.lightRenderQuad,
          lightConfig: shaders.lightConfig,
          isLit: shaders.isLit,
        };
        
        compiledSystems.push(compiled);
      } catch (e) {
        const isCompileError = e instanceof Error && e.name === 'VFXShaderCompileError';
        errors.push({
          code: isCompileError ? 'SHADER_ERROR' : 'INVALID_NODE',
          message: `Shader generation failed: ${e instanceof Error ? e.message : String(e)}`,
          nodeIds: isCompileError
            ? (e as import('./ShaderGenerator').VFXShaderCompileError).nodeIds
            : undefined,
        });
      }
    }
    
    return {
      systems: compiledSystems,
      particleStruct: generateParticleStruct(this.options.attributes),
      errors,
      warnings,
      success: errors.length === 0,
    };
  }
  
  /**
   * Validate a single extracted system.
   */
  private validateSystem(system: VFXExtractedSystem): VFXCompilationError[] {
    const errors: VFXCompilationError[] = [];
    
    // Validate spawn configuration
    if (system.spawn.config.mode === 'none') {
      errors.push({
        code: 'INVALID_SPAWN',
        message: `System "${system.name}" has no spawn configuration. Add a spawn node (Constant Rate, Burst, etc.).`,
        contextId: system.spawn.contextId,
      });
    }
    
    // Validate rate mode has valid rate
    if (system.spawn.config.mode === 'rate') {
      const rate = system.spawn.config.rate ?? 0;
      if (rate <= 0) {
        errors.push({
          code: 'INVALID_SPAWN',
          message: `Spawn rate must be greater than 0 (got ${rate}).`,
          contextId: system.spawn.contextId,
        });
      }
    }
    
    // Validate burst mode has valid count
    if (system.spawn.config.mode === 'burst' || system.spawn.config.mode === 'single_burst') {
      const count = system.spawn.config.burst?.count ?? 0;
      if (count <= 0) {
        errors.push({
          code: 'INVALID_SPAWN',
          message: `Burst count must be greater than 0 (got ${count}).`,
          contextId: system.spawn.contextId,
        });
      }
    }
    
    // Validate Initialize context has nodes
    if (system.initialize.nodes.length === 0) {
      errors.push({
        code: 'NO_INITIALIZE',
        message: `Initialize context is empty. Add at least one initialization node (Set Lifetime, Set Position, etc.).`,
        contextId: system.initialize.contextId,
      });
    }
    
    return errors;
  }

  /**
   * Validate context flow connections.
   * Ensures all required contexts are properly connected.
   */
  private validateContextFlow(graph: VFXGraph): VFXCompilationError[] {
    const errors: VFXCompilationError[] = [];
    
    // Get all contexts
    const spawnCtx = graph.getContextByType('spawn');
    const initCtx = graph.getContextByType('initialize');
    const updateCtx = graph.getContextByType('update');
    const outputCtx = graph.getContextByType('output');
    
    // Required contexts: Spawn, Initialize, Output
    // Optional: Update
    if (!spawnCtx) {
      errors.push({
        code: 'NO_SPAWN',
        message: 'Missing Spawn context. Add a Spawn context to define particle spawning.',
      });
      return errors;
    }
    
    if (!initCtx) {
      errors.push({
        code: 'NO_INITIALIZE',
        message: 'Missing Initialize context. Add an Initialize context to set initial particle values.',
      });
      return errors;
    }
    
    if (!outputCtx) {
      errors.push({
        code: 'NO_OUTPUT',
        message: 'Missing Output context. Add an Output context to render particles.',
      });
      return errors;
    }
    
    // Validate flow: Spawn → Initialize (SpawnEvent)
    if (!graph.hasOutgoingFlow(spawnCtx.id, 'spawn_event')) {
      errors.push({
        code: 'DISCONNECTED_FLOW',
        message: 'Spawn is not connected to Initialize. Connect Spawn\'s SpawnEvent output to Initialize\'s SpawnEvent input.',
        contextId: spawnCtx.id,
      });
    }
    
    if (!graph.hasIncomingFlow(initCtx.id, 'spawn_event')) {
      errors.push({
        code: 'DISCONNECTED_FLOW',
        message: 'Initialize has no incoming SpawnEvent. Connect Spawn → Initialize.',
        contextId: initCtx.id,
      });
    }
    
    // Check if Update is used (has incoming particle connection)
    const updateIsUsed = updateCtx && graph.hasIncomingFlow(updateCtx.id, 'particle');
    
    // Validate flow: Initialize → (Update OR Output) via Particle
    if (!graph.hasOutgoingFlow(initCtx.id, 'particle')) {
      errors.push({
        code: 'DISCONNECTED_FLOW',
        message: 'Initialize is not connected. Connect Initialize\'s Particle output to Update or Output.',
        contextId: initCtx.id,
      });
    }
    
    // If Update is used, it must connect to Output
    if (updateIsUsed) {
      if (!graph.hasOutgoingFlow(updateCtx!.id, 'particle')) {
        errors.push({
          code: 'DISCONNECTED_FLOW',
          message: 'Update is not connected to Output. Connect Update\'s Particle output to Output\'s Particle input.',
          contextId: updateCtx!.id,
        });
      }
    }
    
    // Output must have incoming Particle (from Init or Update)
    if (!graph.hasIncomingFlow(outputCtx.id, 'particle')) {
      errors.push({
        code: 'DISCONNECTED_FLOW',
        message: 'Output has no incoming Particle. Connect Initialize or Update → Output.',
        contextId: outputCtx.id,
      });
    }
    
    return errors;
  }
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Compile a VFX graph with default options.
 */
export function compileVFXGraph(
  graph: VFXGraph,
  options?: VFXCompilerOptions
): Promise<VFXCompilationResult> {
  const compiler = new VFXCompiler(options);
  return compiler.compile(graph);
}

/**
 * Compile a VFX graph with WebGPU shader validation.
 * Use this for pre-flight validation before runtime.
 */
export async function compileVFXGraphAsync(
  graph: VFXGraph,
  options?: VFXCompilerOptions
): Promise<VFXCompilationResult> {
  const { validateShadersAsync } = await import('./WGSLValidator');

  const result = await compileVFXGraph(graph, options);
  
  if (!result.success || result.systems.length === 0) {
    return result;
  }
  
  // Then validate shaders via WebGPU
  const errors = [...result.errors];
  
  for (const system of result.systems) {
    const validation = await validateShadersAsync({
      initShader: system.initShader,
      updateShader: system.updateShader,
      vertexShader: system.vertexShader,
      fragmentShader: system.fragmentShader,
    });
    
    if (!validation.valid) {
      for (const err of validation.errors) {
        errors.push({
          code: 'SHADER_ERROR',
          message: err.message,
        });
      }
    }
  }
  
  return {
    ...result,
    errors,
    success: errors.length === 0,
  };
}

/**
 * Quick validation without full compilation.
 */
export function validateVFXGraph(graph: VFXGraph): VFXCompilationError[] {
  const extraction = extractSystems(graph);
  return extraction.errors;
}
